import { FC, useEffect, useRef, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, DocumentTextIcon, ArrowUpTrayIcon, TrashIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { Layout, RichTextEditor } from '@components';
import {
    getAdminSettings,
    updateAdminLegal,
    uploadLegalDocument,
    deleteLegalDocument,
    legalDocumentUrl,
    LegalDocument,
} from '@services/admin.service';

type Tab = 'cgv' | 'cgu';

export const LegalSettings: FC = () => {
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<Tab>('cgv');
    const [cgv, setCgv] = useState('');
    const [cgu, setCgu] = useState('');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [cgvDocs, setCgvDocs] = useState<LegalDocument[]>([]);
    const [cguDocs, setCguDocs] = useState<LegalDocument[]>([]);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getAdminSettings();
                if (cancelled) return;
                setCgv(res.data.legalCgv || '');
                setCgu(res.data.legalCgu || '');
                setCgvDocs(res.data.legalCgvDocuments || []);
                setCguDocs(res.data.legalCguDocuments || []);
            } catch (err: any) {
                if (!cancelled) setError(err?.message || 'Erreur lors du chargement');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const docs = tab === 'cgv' ? cgvDocs : cguDocs;
    const setDocs = tab === 'cgv' ? setCgvDocs : setCguDocs;

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (!file) return;
        if (file.type !== 'application/pdf') {
            setError('Seuls les fichiers PDF sont autorisés');
            return;
        }
        setUploading(true);
        setError(null);
        setMessage(null);
        try {
            const res = await uploadLegalDocument(tab, file);
            setDocs(res.data.documents);
            setMessage('Document ajouté');
        } catch (err: any) {
            setError(err?.message || "Erreur lors de l'envoi du document");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDoc = async (docId: string) => {
        setDeletingId(docId);
        setError(null);
        setMessage(null);
        try {
            const res = await deleteLegalDocument(tab, docId);
            setDocs(res.data.documents);
            setMessage('Document supprimé');
        } catch (err: any) {
            setError(err?.message || 'Erreur lors de la suppression');
        } finally {
            setDeletingId(null);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setMessage(null);
        try {
            await updateAdminLegal(cgv, cgu);
            setMessage('CGV / CGU enregistrées');
        } catch (err: any) {
            setError(err?.message || "Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 max-w-3xl">
                <div className="flex items-center gap-3 mb-1">
                    <DocumentTextIcon className="w-6 h-6 text-primary" />
                    <h1 className="text-2xl font-bold">CGU / CGV</h1>
                </div>
                <p className="text-sm text-base-content/60 mb-6">
                    Rédigez les Conditions Générales de Vente et d'Utilisation. Elles sont consultables sur la boutique et doivent être acceptées lors de la commande.
                </p>

                {message && (
                    <div className="alert alert-success mb-4">
                        <CheckCircleIcon className="w-5 h-5" />
                        <span>{message}</span>
                    </div>
                )}
                {error && (
                    <div className="alert alert-error mb-4">
                        <XCircleIcon className="w-5 h-5" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="flex gap-2 mb-4">
                    <button
                        type="button"
                        onClick={() => setTab('cgv')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'cgv' ? 'bg-primary text-white' : 'bg-base-200 text-base-content/70'}`}
                    >
                        CGV
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab('cgu')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'cgu' ? 'bg-primary text-white' : 'bg-base-200 text-base-content/70'}`}
                    >
                        CGU
                    </button>
                </div>

                {tab === 'cgv' ? (
                    <RichTextEditor value={cgv} onChange={setCgv} placeholder="Rédigez les Conditions Générales de Vente…" />
                ) : (
                    <RichTextEditor value={cgu} onChange={setCgu} placeholder="Rédigez les Conditions Générales d'Utilisation…" />
                )}

                {/* Documents PDF */}
                <div className="mt-6 border border-base-300 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h2 className="text-sm font-semibold">Documents PDF ({tab.toUpperCase()})</h2>
                            <p className="text-xs text-base-content/60">Ajoutez un ou plusieurs PDF consultables par les clients.</p>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={handleUpload}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="btn btn-sm btn-outline gap-2"
                        >
                            {uploading ? <span className="loading loading-spinner loading-xs"></span> : <ArrowUpTrayIcon className="w-4 h-4" />}
                            Ajouter un PDF
                        </button>
                    </div>
                    {docs.length === 0 ? (
                        <p className="text-xs text-base-content/50 py-2">Aucun document.</p>
                    ) : (
                        <ul className="divide-y divide-base-200">
                            {docs.map((doc) => (
                                <li key={doc.id} className="flex items-center gap-3 py-2">
                                    <DocumentIcon className="w-5 h-5 text-primary flex-shrink-0" />
                                    <a
                                        href={legalDocumentUrl(doc.path)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 min-w-0 text-sm truncate hover:underline"
                                    >
                                        {doc.name}
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteDoc(doc.id)}
                                        disabled={deletingId === doc.id}
                                        className="btn btn-ghost btn-xs text-error"
                                        aria-label="Supprimer"
                                    >
                                        {deletingId === doc.id ? <span className="loading loading-spinner loading-xs"></span> : <TrashIcon className="w-4 h-4" />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="flex justify-end mt-6">
                    <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
                        {saving ? <span className="loading loading-spinner loading-sm"></span> : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </Layout>
    );
};
